import { apiFetchData, withAuthHeader, type AuthRetry } from '@/shared/lib/api';
import { config } from '@/shared/config';
import { useAuthStore } from '@/features/auth/store';
import type {
  DiscountResponse,
  CreateDiscountRequest,
  UpdateDiscountRequest,
  ComboDetailsResponse,
} from './types';

function getAuthRetry(): AuthRetry {
  return {
    getToken: () => useAuthStore.getState().token,
    refreshTokens: () => useAuthStore.getState().refreshTokens(),
  };
}

export const discountApi = {
  // ==================== OWNER ENDPOINTS ====================

  async getDiscounts(
    token: string | null,
    restaurantId: string
  ): Promise<DiscountResponse[]> {
    if (!token) throw new Error('No token');
    const list = await apiFetchData<DiscountResponse[]>(
      `/api/owner/restaurants/${restaurantId}/discounts`,
      { method: 'GET', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
    return Array.isArray(list) ? list : [];
  },

  async createDiscount(
    token: string | null,
    restaurantId: string,
    request: CreateDiscountRequest
  ): Promise<DiscountResponse> {
    if (!token) throw new Error('No token');
    return apiFetchData<DiscountResponse>(
      `/api/owner/restaurants/${restaurantId}/discounts`,
      { method: 'POST', headers: withAuthHeader(undefined, token), body: request },
      getAuthRetry()
    );
  },

  async updateDiscount(
    token: string | null,
    restaurantId: string,
    discountId: string,
    request: UpdateDiscountRequest
  ): Promise<DiscountResponse> {
    if (!token) throw new Error('No token');
    return apiFetchData<DiscountResponse>(
      `/api/owner/restaurants/${restaurantId}/discounts/${discountId}`,
      { method: 'PUT', headers: withAuthHeader(undefined, token), body: request },
      getAuthRetry()
    );
  },

  async deleteDiscount(
    token: string | null,
    restaurantId: string,
    discountId: string
  ): Promise<void> {
    if (!token) throw new Error('No token');
    await apiFetchData<unknown>(
      `/api/owner/restaurants/${restaurantId}/discounts/${discountId}`,
      { method: 'DELETE', headers: withAuthHeader(undefined, token) },
      getAuthRetry()
    );
  },

  async deleteImage(
    token: string | null,
    restaurantId: string,
    discountId: string
  ): Promise<void> {
    if (!token) throw new Error('No token');
    const url = `${config.apiBaseUrl}/api/owner/restaurants/${restaurantId}/discounts/${discountId}/image`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to delete image');
    }
  },

  async uploadImage(
    token: string | null,
    restaurantId: string,
    discountId: string,
    file: File
  ): Promise<DiscountResponse> {
    if (!token) throw new Error('No token');
    const formData = new FormData();
    formData.append('file', file);
    const url = `${config.apiBaseUrl}/api/owner/restaurants/${restaurantId}/discounts/${discountId}/image`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error((err as { message?: string }).message ?? 'Failed to upload image');
    }
    return response.json() as Promise<DiscountResponse>;
  },

  // ==================== PUBLIC ENDPOINTS ====================

  async getActiveDiscounts(restaurantId: string): Promise<DiscountResponse[]> {
    const list = await apiFetchData<DiscountResponse[]>(
      `/api/public/restaurants/${restaurantId}/discounts`,
      { method: 'GET' }
    );
    return Array.isArray(list) ? list : [];
  },

  async getDealsOfTheDay(restaurantId: string): Promise<DiscountResponse[]> {
    const list = await apiFetchData<DiscountResponse[]>(
      `/api/public/restaurants/${restaurantId}/discounts/deals-of-the-day`,
      { method: 'GET' }
    );
    return Array.isArray(list) ? list : [];
  },

  async getCombos(restaurantIdOrSlug: string): Promise<ComboDetailsResponse[]> {
    try {
      const list = await apiFetchData<ComboDetailsResponse[]>(
        `/api/public/restaurants/${restaurantIdOrSlug}/discounts/combos`,
        { method: 'GET' }
      );
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  },
};
